/**
 * Member-facing waste (solid waste / landfill) copy for the Waste page.
 * Distinguishes tonnage, inventory GHG, and facility charts — no DB table names.
 */

export type WasteChartView = 'tonnage' | 'ghg' | 'stacked';

/** Same method as the GHG inventory landfill (waste) sector. */
export const WASTE_INVENTORY_METHOD_SHORT =
  'City waste tons (CalRecycle jurisdiction of origin) × county landfill GHG intensity (EPA GHGRP facility emissions ÷ facility waste tons for that county and year; statewide intensity if the county has no usable factor).';

export const GHG_PAGE_WASTE_METHODOLOGY_DETAILED =
  `Landfill / solid waste (inventory method): ${WASTE_INVENTORY_METHOD_SHORT} This is the landfill portion of Waste & wastewater on the GHG inventory page. It is not a sum of individual landfill stacks for the city. Facility-level EPA charts on the Waste page are contextual only and are not added into the inventory total.`;

export function wasteOverviewDescription(view: WasteChartView): string {
  if (view === 'ghg') {
    return (
      'This chart uses the same landfill calculation as the GHG inventory page: ' +
      'local waste tons × a county landfill carbon intensity. ' +
      'Intensity is EPA facility emissions divided by facility waste tons in the county (or a statewide factor if needed). ' +
      'The orange line is that intensity (tons CO₂e per ton of waste). ' +
      'Totals here should match the landfill portion of Waste & wastewater on the GHG inventory (wastewater is added separately there).'
    );
  }
  if (view === 'stacked') {
    return (
      'This chart shows EPA-reported GHG emissions by landfill facility for the county that contains this city ' +
      '(or for the county itself when a county is selected). ' +
      'It is NOT the city inventory total and is NOT added into the sector-based GHG inventory. ' +
      'County facility stacks and city inventory estimates use different scopes, so their totals will not match. ' +
      'The inventory instead applies a county-average intensity to the city’s CalRecycle tons. ' +
      'Landfill gas controls are largely regulated at the state level and are often outside local government control.'
    );
  }
  return (
    'This chart shows CalRecycle disposal tonnage attributed to this jurisdiction (jurisdiction of origin) ' +
    'and tons per service population (residents plus employees). ' +
    'High per-service rates often reflect commercial and industrial disposal attributed to businesses in the city, ' +
    'not only household trash—so industrial or port cities can look much higher than the statewide average.'
  );
}

export function wasteMethodologyDescription(view: WasteChartView): string {
  if (view === 'ghg') {
    return 'Inventory-aligned landfill GHG: CalRecycle tons for this place × county (or statewide) landfill intensity from EPA GHGRP.';
  }
  if (view === 'stacked') {
    return 'County landfill facility emissions from EPA GHGRP for context only—not used in the GHG inventory total.';
  }
  return 'CalRecycle jurisdiction-of-origin disposal tons and tons per service population (residents plus employees).';
}

export const WASTE_TONNAGE_METHOD_STEPS: string[] = [
  'Resolve the selected city or county.',
  'Load CalRecycle jurisdiction-of-origin disposal tons for that place.',
  'Divide by service population (residents plus employees) for the per-service metric.',
  'Compare to the California average when available.',
];

export const WASTE_GHG_METHOD_STEPS: string[] = [
  'Load CalRecycle waste tons for this city (or county).',
  'Build a county landfill intensity for each year: sum of EPA GHGRP landfill CO₂e ÷ sum of facility waste tons in that county.',
  'If the county has no usable intensity, use a California-wide intensity for that year.',
  'City landfill GHG = city waste tons × that intensity (same formula as the GHG inventory landfill sector).',
  'Show intensity on the secondary axis. Early years may use population-based tonnage backcasts when CalRecycle rows start later.',
];

export const WASTE_FACILITY_METHOD_STEPS: string[] = [
  'Resolve the county that contains the selected city (or use the county directly).',
  'Load EPA GHGRP landfill facility CO₂e for that county by year.',
  'Stack facilities for context. Do not treat the stack total as the city’s inventory waste emissions.',
  'Inventory waste uses city tons × county intensity instead (see GHG Emissions tab and the GHG inventory page).',
];

export function wasteTonnageHighRateNote(
  location: string,
  latestPerService: number,
  californiaPerService: number
): string {
  if (!(latestPerService > 0 && californiaPerService > 0)) return '';
  const ratio = latestPerService / californiaPerService;
  if (ratio < 1.5) return '';
  return (
    ` ${location}'s rate is much higher than the state average. ` +
    'CalRecycle attributes disposal to the jurisdiction where waste originated, including commercial and industrial loads. ' +
    'Cities with ports, refining, manufacturing, or large employment bases often show elevated tons per service population even when household generation is closer to typical.'
  );
}
